import { useMemo } from 'react';
import { logger } from '@marriott/mi-headless-utils';
import {
  normalizeHwsRlmCandidates,
  normalizeReslinkCandidates,
  resolveNowMs,
  selectHomepageRetargetingCandidate,
} from '../utils/retargeting/retargetingCandidates';
import type {
  HomepageRetargetingCandidate,
  HomepageRetargetingUseCase,
  UseRetargetingCandidateArgs,
} from '../utils/retargeting/retargeting.types';
import {
  HOMEPAGE_HWS_RLM_USE_CASE,
  RETARGETING_CANDIDATE_READER_ERROR_EVENT,
} from '../utils/retargeting/retargeting.constants';
import { readClientRlmSearchCriteria } from '../utils/retargeting/retargetingRlmPathing';
import { RESLINK } from '@marriott/mi-localstorage-utils';

const { log } = logger({})('useRetargetingCandidate');

export function useRetargetingCandidate({
  now,
  eligibleUseCases,
  refreshKey,
  sessionDatalayer,
}: UseRetargetingCandidateArgs): HomepageRetargetingCandidate | null {
  const normalizedNow = resolveNowMs(now);
  const eligibleUseCasesKey = eligibleUseCases.join('|');
  const refreshKeyToken = refreshKey ?? null;

  return useMemo(() => {
    try {
      void refreshKeyToken;
      const normalizedEligibleUseCases = (
        eligibleUseCasesKey ? eligibleUseCasesKey.split('|') : []
      ) as HomepageRetargetingUseCase[];
      const eligibleUseCaseSet = new Set(normalizedEligibleUseCases);
      const candidates: HomepageRetargetingCandidate[] = [];
      if (eligibleUseCaseSet.has(HOMEPAGE_HWS_RLM_USE_CASE)) {
        candidates.push(...normalizeHwsRlmCandidates(normalizedNow));
      }
      if (eligibleUseCaseSet.has(RESLINK)) {
        candidates.push(...normalizeReslinkCandidates(normalizedNow));
      }

      return selectHomepageRetargetingCandidate({
        now: normalizedNow,
        eligibleUseCases: normalizedEligibleUseCases,
        candidates,
        searchCriteria: readClientRlmSearchCriteria(sessionDatalayer),
      });
    } catch (error) {
      log.warn(RETARGETING_CANDIDATE_READER_ERROR_EVENT, {
        errorType: error instanceof Error ? error.name : typeof error,
      });
      return null;
    }
  }, [normalizedNow, eligibleUseCasesKey, refreshKeyToken, sessionDatalayer]);
}
